<template>
	<div
		v-if="userData && profileData && lifeStageData && categoryGroups"
		class="card-page"
	>
		<ja-send-message-modal
			:name="$t('sendMessageModalName')"
			:show="showSendMessageModal"
			@closeModal="toggleSendMessageModal"
			@modalChanged="onModalChanged"
		></ja-send-message-modal>
		<ja-header
			:title="$t('gamePlayMetaTitles.profile')"
			:show-home="true"
			:show-intro="true"
			:disabled="isModalOpened"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/home"></svgicon>
			</template>
		</ja-header>
		<content ref="view" class="page-wrapper">
			<div class="profile-page">
				<h1 class="heading-2">{{ $t('profile') }}</h1>
				<div class="profile-progress">
					<div class="profile-column" aria-hidden="true">
						<ja-avatar :size="140"></ja-avatar>
					</div>
					<div class="profile-column">
						<ja-budget-counter class="progress-bar"></ja-budget-counter>
					</div>
					<div class="profile-column">
						<ja-financial-health
							v-if="!profileData.isEntryVirtualSimulationType"
							class="progress-bar"
						></ja-financial-health>
					</div>
				</div>
				<div v-if="profileInfo" class="profile-info-wrapper">
					<div
						v-for="info in profileInfo"
						:key="info.name"
						class="profile-info"
					>
						<div class="heading-5">{{ info.name }}</div>
						<div class="body-text-1">
							{{ info.value }}
						</div>
					</div>
					<div class="tooltip-wrapper">
						<button
							class="tooltip-button"
							:aria-label="$t('moreInfoSavings')"
							@click="toggleTooltip"
							@mousedown.prevent
						>
							<svgicon name="savings-info" :original="true"> </svgicon>
						</button>
						<ja-tooltip v-show="showTooltip" role="status" class="left">{{
							$t(careerInfo ? careerInfo : 'noJobDescription')
						}}</ja-tooltip>
					</div>
				</div>
				<div class="send-message-wrapper">
					<label for="sendMessageBtn" class="body-text-1">{{
						$t('sendMessageLabel')
					}}</label>
					<ja-button
						id="sendMessageBtn"
						class="transparent body-text-2"
						name="sendMessage"
						@click.native="toggleSendMessageModal"
					>
						{{ $t('sendMessage') }}
					</ja-button>
				</div>
				<ja-accordion
					v-if="profileData.isEntryVirtualSimulationType"
					:tabs="filteredTabs"
				>
				</ja-accordion>
				<ja-accordion v-else :tabs="tabs"> </ja-accordion>
			</div>
		</content>
		<footer>
			<div v-if="localSponsor" class="footer-item">
				<ja-local-sponsor :logo="localSponsor"></ja-local-sponsor>
			</div>
			<ja-chat-bot
				v-if="userData.userActiveSimulationAIChatbotEnable"
				:is-chat-in-footer="true"
				:hide-chatbot-button="$store.state.loading"
			/>
			<div class="footer-item footer-item--button">
				<ja-button
					class="small"
					name="close-calendar"
					:disabled="isModalOpened"
					:aria-label="$t('closeProfile')"
					@click.native.once="closeProfile"
					>{{ $t('close') }}</ja-button
				>
			</div>
		</footer>
	</div>
</template>
<script>
import gql from 'graphql-tag';
import UserData from '../mixins/UserData';
import ExpenseCategories from '../mixins/ExpenseCategories';
import ProfileData from '../mixins/ProfileData';
import LifeStageData from '../mixins/LifeStageData';
import ModalFocusManager from '../mixins/ModalFocusManager';
import Avatar from '../components/ui/Avatar';
import Header from '../components/ui/headers/Header';
import SendMessageModal from '../components/Modal/SendMessageModal';
import Accordion from '../components/ui/Accordion';
import Button from '../components/ui/Button';
import BudgetTab from '../components/ProfileTabs/BudgetTab';
import LifestyleGoalsTab from '../components/ProfileTabs/LifestyleGoalsTab';
import FinancialTab from '../components/ProfileTabs/FinancialTab';
import BudgetCounter from '../components/Metrics/BudgetCounter';
import FinancialHealth from '../components/Metrics/FinancialHealthMeter';
import LocalSponsor from '@/components/ui/LocalSponsor';
import Tooltip from '@/components/ui/Tooltip';
import ChatBot from '@/components/ChatBot/ChatBot';

export default {
	components: {
		jaAvatar: Avatar,
		jaHeader: Header,
		jaSendMessageModal: SendMessageModal,
		jaAccordion: Accordion,
		jaButton: Button,
		jaBudgetCounter: BudgetCounter,
		jaFinancialHealth: FinancialHealth,
		jaLocalSponsor: LocalSponsor,
		jaTooltip: Tooltip,
		jaChatBot: ChatBot
	},
	mixins: [
		UserData,
		ProfileData,
		ExpenseCategories,
		LifeStageData,
		ModalFocusManager
	],
	data() {
		return {
			showSendMessageModal: false,
			tabs: [
				{
					title: this.$t('financialOverview'),
					component: FinancialTab,
					show: true
				},
				{
					title: this.$t('budget'),
					component: BudgetTab,
					show: true
				},
				{
					title: this.$t('lifestyleGoals'),
					component: LifestyleGoalsTab,
					show: false
				}
			],
			showTooltip: false,
			cmsCareerClusters: [],
			careerInfo: null,
			maritalStatus: 'married'
		};
	},
	apollo: {
		cmsCareerClusters: {
			query: gql`
				query clusters($jtWhere: String!) {
					cmsCareerClusters(
						where: "CmsCareers.Count(it.isActive=\\"true\\")>0"
					) {
						cmsCareers(
							where: "isActive=\\"true\\" && CmsCareerJobtitles.Count(it.isActive=\\"true\\")==4"
						) {
							career_ID
							name
							description
							cmsCareerJobTitles(
								where: $jtWhere
								order: "CmsLifeStage.DisplayOrder"
							) {
								jobTitle
								careerJobTitle_ID
							}
						}
					}
				}
			`,
			variables() {
				return {
					jtWhere: `isActive="true"`
				};
			},
			update(data) {
				return data.cmsCareerClusters;
			}
		}
	},
	computed: {
		debts() {
			return this.$store.getters.debts;
		},
		profileInfo() {
			if (this.userData && this.profileData) {
				const profileArray = [
					{
						name: this.$t('name'),
						value:
							this.lifeStageData.formStudentName ??
							`${this.userData.firstName} ${this.userData.lastName}`
					},
					{
						name: this.$t('job'),
						value: this.profileData.cmsCareerJobTitle.jobTitle
					},
					{
						name: this.$t('creditScore'),
						value: this.profileData.currentCreditScore
					},
					{
						name: this.$t('GAI'),
						value: this.$options.filters.currency(
							this.profileData.cmsCareerJobTitle.salary
						)
					},
					{
						name: this.$t('lifeStage'),
						value: this.profileData.cmsLifeStage.name
					},
					{
						name: this.$t('status'),
						value: this.profileData.cmsMaritalStatus.status
					},
					{
						name: this.$t('studentSalary'),
						value: this.$options.filters.currency(
							this.profileData.netMonthlyIncome
						)
					},
					{
						name: this.$t('education'),
						value: this.profileData.cmsEducation.name
					},
					{ name: this.$t('children'), value: this.childrenInfo }
				];
				if (
					this.profileData.cmsMaritalStatus.status.toLowerCase() ===
					this.maritalStatus
				) {
					profileArray.push({
						name: this.$t('studentSpouseNMI'),
						value: this.$options.filters.currency(
							this.profileData.spouseNetMonthlyIncome
						)
					});
				}
				const filteredProfileArray = this.$store.getters.isIrl
					? profileArray.filter(item => item.name !== this.$t('creditScore'))
					: profileArray;

				return filteredProfileArray;
			}
			return null;
		},
		localSponsor() {
			return this.$store.getters.localSponsor('Profile');
		},
		filteredTabs() {
			return this.tabs.filter(i => i.show);
		},
		children() {
			if (this.profileData) {
				return this.profileData.studentChildren;
			}
			return null;
		},
		infantsCount() {
			return this.children.filter(child => child.ageGroup === 'Infant').length;
		},
		childrenCount() {
			return this.children.filter(child => child.ageGroup === 'Child').length;
		},
		teenagersCount() {
			return this.children.filter(child => child.ageGroup === 'Teenager')
				.length;
		},
		childrenInfo() {
			let childrenCountByAgeGroup = [];
			if (this.infantsCount || this.childrenCount || this.teenagersCount) {
				if (this.infantsCount) {
					let infantsLiteral =
						this.infantsCount > 1 ? this.$t('infants') : this.$t('infant');
					childrenCountByAgeGroup.push(
						`${this.infantsCount} ${infantsLiteral}`
					);
				}
				if (this.childrenCount) {
					let childrenLiteral =
						this.childrenCount > 1 ? this.$t('children') : this.$t('child');
					childrenCountByAgeGroup.push(
						`${this.childrenCount} ${childrenLiteral}`
					);
				}
				if (this.teenagersCount) {
					let teenagersLiteral =
						this.teenagersCount > 1
							? this.$t('teenagers')
							: this.$t('teenager').toLowerCase();
					childrenCountByAgeGroup.push(
						`${this.teenagersCount} ${teenagersLiteral}`
					);
				}
			} else {
				childrenCountByAgeGroup.push(this.$t('none'));
			}

			var result = childrenCountByAgeGroup.join(', ');
			return result;
		}
	},
	created() {
		this.checkForLifeStageData();
		this.checkForProfileData();
		this.checkForCategories();
	},
	beforeCreate() {
		this.$store.commit('setLoading', true);
	},
	mounted() {
		this.$store.commit('setLoading', false);
	},
	updated() {
		this.$store.commit('setLoading', false);
	},
	methods: {
		closeProfile() {
			this.$router.push({ name: 'home' });
		},
		toggleSendMessageModal() {
			this.showSendMessageModal = !this.showSendMessageModal;
		},
		findCareer() {
			const jobTitle = this.profileData.cmsCareerJobTitle.jobTitle;
			const foundCareer = this.cmsCareerClusters
				.map(obj =>
					obj.cmsCareers.find(career =>
						career.cmsCareerJobTitles.some(job => job.jobTitle === jobTitle)
					)
				)
				.find(career => !!career);
			if (foundCareer) {
				this.careerInfo = foundCareer.description;
			}
		},
		toggleTooltip() {
			this.showTooltip = !this.showTooltip;
			if (!this.careerInfo) this.findCareer();
		}
	}
};
</script>
<style lang="scss" scoped>
.page-wrapper {
	position: relative;
	width: 100%;
	padding: 32px 5%;

	.home-button {
		right: 5%;
		left: auto;
	}

	.profile-page {
		width: 66%;
		max-width: 850px;
		margin: auto;

		.heading-2 {
			margin-top: 0;
			margin-bottom: 16px;
		}

		.profile-progress {
			display: flex;
			align-items: center;

			.profile-column {
				width: 33.33%;
				z-index: 1;
			}
		}

		.profile-info-wrapper {
			display: flex;
			flex-wrap: wrap;
			margin-top: 24px;
			padding-bottom: 16px;
			border-bottom: 1px solid $container-border;

			.profile-info {
				width: 33.33%;
				margin-bottom: 16px;
				padding-right: 16px;

				.heading-5 {
					color: $font-gray;
					margin-bottom: 8px;
				}
			}

			.children-info {
				text-transform: capitalize;
			}
			.tooltip-wrapper {
				position: absolute;
				display: inline-block;
				top: 253px;
				left: 725px;
				.tooltip-button {
					background-color: transparent;
					border: 0;
					padding: 0;
					margin-left: 4px;

					::v-deep path {
						fill: $info-icon-color;
					}
				}

				.tooltip-msg {
					transform: translate(36px, -50%);
					top: 14px;
					left: 10px;
					text-align: center;

					@media (min-width: $breakpoint-mid) {
						width: 320px;
						font-size: 24px;
					}
				}

				svg {
					height: 24px;
					width: 24px;
					&:hover {
						cursor: pointer;
					}
				}
			}
		}

		.send-message-wrapper {
			display: flex;
			align-items: center;
			width: 100%;
			padding: 16px 0;
			border-bottom: 1px solid $container-border;

			.body-text-1 {
				flex-grow: 1;
			}

			.body-text-2 {
				font-family: 'RobotoMedium';
				color: $font-blue;

				&:hover {
					color: $hover-state;
					cursor: pointer;
				}
			}
		}
	}
}
footer {
	height: 90px;

	.footer-item {
		width: 50%;

		&:last-child {
			text-align: right;
		}

		img {
			display: block;
			max-height: 82px;
			margin-top: 4px;
		}
	}
}
</style>
